import { calcRequiredDownPmt, handleMonthlyPmt } from '.';
import { calculateInstallment, calcInstallmentWithDown } from './mmf';

/**
 * This function updates the option object when there is no split loan
 * @param {object} active
 * @param {object} option
 * @param {number} minRate
 * @param {number} maxRate
 * @param {number} accurateFinalTotalCost
 */

export const handleDownAndMonthlyPmt = (
  active,
  option,
  minRate,
  maxRate,
  accurateFinalTotalCost
) => {
  const isMyMilanPrimary = active.lender.name === 'myMILAN';
  const lenderDownPayment = active.lender.downpayment;
  const isIntRateZero = minRate === 0 && maxRate === 0;

  // 1. Handle down payment logic
  // When lender requires minimum down payment as a PERCENTAGE (Covered Care for example)
  // and the Down Payment input is enabled and amount entered is larger than the original requiredDownPmt
  if (
    lenderDownPayment > 0 &&
    option.downPmtEnabled &&
    option.downpayment > option.requiredDownPmt
  ) {
    // Split the down payment into 2 parts: (required & additional)
    const { requiredDownPmt } = calcRequiredDownPmt(
      option.finalTotalCost,
      option.downpayment,
      lenderDownPayment
    );
    option.requiredDownPmt = requiredDownPmt;
  } else {
    // When the Down Payment input is disabled
    const isPercentage = lenderDownPayment > 0 && lenderDownPayment < 1;
    option.requiredDownPmt = isPercentage
      ? lenderDownPayment * option.finalTotalCost
      : lenderDownPayment;
  }

  // If the amount of down payment entered manually is smaller than the required one,
  // Today's Payment defaults to required amount
  option.todaysPayment =
    option.downpayment < option.requiredDownPmt
      ? option.requiredDownPmt
      : option.downpayment;

  // 2. Calculate the monthly payments
  option.monthlyPayment = handleMonthlyPmt(
    option.finalTotalCost,
    option.todaysPayment,
    minRate,
    maxRate,
    option.financePlan
  );

  // ------------ myMILAN Logic ------------ //
  if (isMyMilanPrimary) {
    // By default, myMILAN requires a down payment equal to the first month payment
    // Unlike other lenders, this required down payment (today's payment) must be included in the total financed amount
    option.totalFinanced = accurateFinalTotalCost; // Very important // Do NOT remove this line
    // DO NOT use: option.totalFinanced = option.finalTotalCost // It does NOT give accurate result

    // 1. Get the required down payment first
    // Required Down Payment is basically the monthly payment
    if (!isIntRateZero) {
      // When interest rate is NOT 0%, we can directly use calcInstallment to get the monthly payment
      option.requiredDownPmt = calculateInstallment(
        option.totalFinanced,
        active.lender,
        active.financePlan
      );
    } else {
      // When interest rate is 0%, we need to calculate the monthly payment based on 0 down payment first
      const monthlyPmt = handleMonthlyPmt(
        option.finalTotalCost,
        0, // Start with 0 down payment to get the monthly payment
        minRate,
        maxRate,
        option.financePlan
      )[0];
      // For myMILAN with 0% interest rate, the required down payment is equal to the monthly payment calculated based on 0 down payment
      option.requiredDownPmt = monthlyPmt;
    }

    // In case an additional down payment is entered
    // Check if the entered down payment is smaller than the required one
    const isDownPaymentSmallerThanRequired =
      option.downpayment <= option.requiredDownPmt;
    // The today's payment must be equal to or greater than the required down payment
    option.todaysPayment = isDownPaymentSmallerThanRequired
      ? option.requiredDownPmt
      : option.downpayment;

    // 2. Update the monthly payment based on the down payment entered
    // Treat the required down payment (today's payment in this case) as the additional down payment
    if (!isIntRateZero) {
      // When interest rate is NOT 0%, we can directly use calcInstallmentWithDown to get the monthly payment
      option.monthlyPayment = [
        calcInstallmentWithDown(
          accurateFinalTotalCost,
          option.todaysPayment, // Use today's payment as the remaining down payment parameter here
          active.lender,
          active.financePlan
        ),
      ];
    } else {
      // When interest rate is 0%, exclude the required down payment, in other words,
      // Calculate the monthly payment based on the remaining amount (additional down payment)
      option.monthlyPayment = handleMonthlyPmt(
        option.finalTotalCost,
        option.additionalDownPmt, // Use additional down payment for the downpayment parameter here
        minRate,
        maxRate,
        option.financePlan
      );
    }
    // Update Required Down Payment
    option.requiredDownPmt = option.monthlyPayment[0];
    // Update Today's Payment
    option.todaysPayment = isDownPaymentSmallerThanRequired
      ? option.requiredDownPmt
      : option.downpayment;
    // Update Total Financed
    option.totalFinanced =
      accurateFinalTotalCost - option.todaysPayment + option.monthlyPayment[0];
  }
};
